import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { TrashIcon } from "lucide-react";
import { AuditCell } from "./AuditCell";
import { ConfirmActionDialog } from "./ConfirmActionDialog";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import type { components } from "@dolfin/api-admin";

type InvoiceState = components["schemas"]["InvoiceState"];

interface InvoiceRow {
  id: string;
  organisationId: string;
  documentNumber: string;
  customerId: string;
  customer: { name: string } | null;
  status: string;
  totalAmount: number;
  currency: string;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface InvoicesTableFilters {
  states?: InvoiceState[];
  invoiceNumber?: string;
  issueDateFrom?: string;
  issueDateTo?: string;
}

interface InvoicesTableProps {
  organisationId?: string;
  filters?: InvoicesTableFilters;
}

const BASE_COLUMNS = 8;

function statusVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Paid":
      return "default";
    case "Issued":
      return "secondary";
    case "Void":
      return "destructive";
    default:
      return "outline";
  }
}

export function InvoicesTable({ organisationId, filters }: InvoicesTableProps) {
  const showOrganisation = !organisationId;
  const columnCount = BASE_COLUMNS + (showOrganisation ? 1 : 0);

  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InvoiceRow | null>(null);

  const pagination = useCursorPagination({ initialLimit: 20 });
  const { cursor, limit, setPage, reset } = pagination;

  // Reset the cursor during render, not in an effect: an effect would send the stale cursor once, and a
  // cursor only means something within one filtered list.
  const states = filters?.states?.join(",");
  const { invoiceNumber, issueDateFrom, issueDateTo } = filters ?? {};
  const listKey = [organisationId, states, invoiceNumber, issueDateFrom, issueDateTo].join("|");
  const [cursorListKey, setCursorListKey] = useState(listKey);
  if (cursorListKey !== listKey) {
    setCursorListKey(listKey);
    reset();
  }

  useEffect(() => {
    let cancelled = false;

    async function fetchInvoices() {
      setLoading(true);
      setError(null);

      const { data, error: apiError } = await api.GET("/admin/invoices", {
        params: {
          query: {
            OrganisationId: organisationId,
            States: states ? (states.split(",") as InvoiceState[]) : undefined,
            InvoiceNumber: invoiceNumber,
            IssueDateFrom: issueDateFrom,
            IssueDateTo: issueDateTo,
            Cursor: cursor,
            Limit: limit,
          },
        },
      });

      if (cancelled) return;
      if (apiError) {
        setError("Failed to load invoices.");
      } else {
        setInvoices((data?.data ?? []) as InvoiceRow[]);
        setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchInvoices();
    return () => {
      cancelled = true;
    };
  }, [organisationId, states, invoiceNumber, issueDateFrom, issueDateTo, cursor, limit, setPage]);

  async function handleDelete(id: string) {
    setDeleting(id);
    const { error: apiError } = await api.DELETE("/admin/invoices/{id}", {
      params: { path: { id } },
    });

    if (apiError) {
      setError("Failed to delete invoice.");
    } else {
      setInvoices((prev) => prev.filter((i) => i.id !== id));
    }
    setDeleting(null);
    setDeleteTarget(null);
  }

  return (
    <div>
      <div className="mb-2 flex h-5 items-center text-sm text-muted-foreground">
        {loading && <Spinner className="size-4" />}
      </div>

      {error && <p className="text-destructive mb-4 text-sm">{error}</p>}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Document #</TableHead>
              <TableHead>Invoice ID</TableHead>
              <TableHead>Customer</TableHead>
              {showOrganisation && <TableHead>Organisation</TableHead>}
              <TableHead>Status</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead>Last modified</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="text-muted-foreground h-24 text-center">
                  No invoices found.
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell className="font-mono font-medium">
                    <Link to={`/invoices/${inv.id}`} className="hover:underline">
                      {inv.documentNumber}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/invoices/${inv.id}`}>
                      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                        {inv.id.slice(0, 8)}...
                      </Badge>
                    </Link>
                  </TableCell>
                  <TableCell>
                    {inv.customerId ? (
                      <Link
                        to={`/customers/${inv.customerId}?organisationId=${inv.organisationId}`}
                        className="hover:underline"
                      >
                        {inv.customer?.name ?? inv.customerId.slice(0, 8) + "..."}
                      </Link>
                    ) : (
                      inv.customer?.name ?? "-"
                    )}
                  </TableCell>
                  {showOrganisation && (
                    <TableCell>
                      <Link to={`/organisations/${inv.organisationId}`}>
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {inv.organisationId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    </TableCell>
                  )}
                  <TableCell>
                    <Badge variant={statusVariant(inv.status)}>{inv.status}</Badge>
                  </TableCell>
                  <TableCell>{inv.totalAmount}</TableCell>
                  <TableCell>{inv.currency}</TableCell>
                  <TableCell>
                    <AuditCell
                      createdAt={inv.createdAt}
                      createdBy={inv.createdBy}
                      updatedAt={inv.updatedAt}
                      updatedBy={inv.updatedBy}
                    />
                  </TableCell>
                  <TableCell>
                    {inv.status === "Draft" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        disabled={deleting === inv.id}
                        onClick={() => setDeleteTarget(inv)}
                      >
                        {deleting === inv.id ? (
                          <Spinner className="size-4" />
                        ) : (
                          <TrashIcon className="size-4" />
                        )}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />

      <ConfirmActionDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete this draft invoice?"
        description={
          <>Deleting draft invoice <span className="font-mono font-medium">{deleteTarget?.documentNumber}</span> cannot be undone.</>
        }
        confirmLabel="Delete"
        pending={!!deleting}
        onConfirm={() => { if (deleteTarget) handleDelete(deleteTarget.id); }}
      />
    </div>
  );
}
